/**
 * Delete PO Confirmation Modal (Shared)
 *
 * Moved from Inventory page to shared location for use by both
 * Inventory and Cashflow pages.
 */

import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { PurchaseOrder } from "models/InventoryModel";

interface DeletePOConfirmationProps {
  poToDelete: PurchaseOrder | null;
  isDeletingPO: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export default function DeletePOConfirmation({
  poToDelete,
  isDeletingPO,
  onConfirm,
  onCancel,
}: DeletePOConfirmationProps): JSX.Element {
  return (
    <Dialog
      open={poToDelete !== null}
      onClose={() => !isDeletingPO && onCancel()}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ color: "error.main" }}>
        Delete Purchase Order?
      </DialogTitle>
      <DialogContent>
        {poToDelete && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="body1">
              Are you sure you want to delete this purchase order? This action cannot be undone.
            </Typography>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="subtitle2" sx={{ opacity: 0.7 }}>PO Number:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{poToDelete.poNumber}</Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="subtitle2" sx={{ opacity: 0.7 }}>Arrive By Date:</Typography>
                  <Typography variant="body2">{poToDelete.arriveByDate}</Typography>
                </Box>
                {poToDelete.description && (
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="subtitle2" sx={{ opacity: 0.7 }}>Description:</Typography>
                    <Typography variant="body2">{poToDelete.description}</Typography>
                  </Box>
                )}
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="subtitle2" sx={{ opacity: 0.7 }}>Total Quantity:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {poToDelete.lineItems.reduce((sum, li) => sum + li.quantity, 0).toLocaleString()}
                  </Typography>
                </Box>
              </Box>

              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, opacity: 0.7 }}>
                Line Items ({poToDelete.lineItems.length}):
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Inventory ID</TableCell>
                      <TableCell align="right">Quantity</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {poToDelete.lineItems.map((li, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{li.inventoryId}</TableCell>
                        <TableCell align="right">{li.quantity.toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onCancel}
          disabled={isDeletingPO}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={onConfirm}
          disabled={isDeletingPO}
        >
          {isDeletingPO ? "Deleting..." : "Delete PO"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
